import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { ActionTypes } from "../../../../constants";

import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Container,
    Grid,
    LinearProgress,
    Stack,
    Typography,
} from "@mui/material";
import ActivityWeeklyChart from "../Charts/ActivityWeeklyChart";
import AlertMessage from "../../../Common/Alert/AlertMessage";
import BreadcrumbBuilder from "../BreadcrumbBuilder";
import DoughnutChart from "../Charts/DoughnutChart";
import JobPlanButton from "./JobPlanButton";
import JobPlanDetails from "./JobPlanDetails";
import JPAvatar from "../JPAvatar";

import RenderDate from "../../../Common/Date/RenderDate";

import { NumericFormat } from "react-number-format";
import { v4 as uuidv4 } from "uuid";
import { getDayOfWeek } from "../functions";

import Error404 from "../../../Errors/Error404";
import Error500 from "../../../Errors/Error500";

import { getJobPlanDetails } from "../../../../actions/jpActions";
import { getJobPlan, getError } from "../../../../reducers";

const appCode = "P-JP";

function JobPlanInsights({ jobPlan }) {
    const activities = jobPlan.activities || [];
    const activityCount = activities?.length || 0;

    let activityCategories = [];
    let contractedActivityCategories = [];
    let contractedActivityLocations = [];

    let contractedDurationDailyAverage = {
        PA: { Sun: 0, Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0 },
        APA: { Sun: 0, Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0 },
    };
    let durationWeeklyAverage = { PA: 0, APA: 0 };

    activities.forEach((activity) => {
        const durationAveragePW =
            activity.durationNormal + activity.durationPremium;

        if (!activity.type) {
            activity.type = {
                name: "Programmed Activity",
                code: "PA",
                contracted: true,
            };
        }
        if (typeof activity.category === "string") {
            activity.category = {
                id: uuidv4(),
                code: activity.categoryType || "X",
                name: activity.category,
            };
        }
        if (!activity.duration) {
            activity.duration = 0;
        }
        if (!activity.durationPremium) {
            activity.durationPremium = activity.paPremium || 0;
        }
        if (!activity.durationNormal) {
            activity.durationNormal = activity.paNormal || 0;
        }
        if (activity.durationNormal + activity.durationPremium === 0) {
            activity.durationNormal = activity.duration;
        }

        if (activity.scheduled?.dayOfWeek) {
            const dayOfWeek = getDayOfWeek(activity.scheduled.dayOfWeek);
            if (activity.type.code === "PA") {
                contractedDurationDailyAverage.PA[dayOfWeek] +=
                    durationAveragePW;
            }
            if (activity.type.code === "APA") {
                contractedDurationDailyAverage.APA[dayOfWeek] +=
                    durationAveragePW;
            }
        }

        if (activity.type.contracted) {
            if (contractedActivityCategories[activity.category.name]) {
                contractedActivityCategories[activity.category.name] +=
                    durationAveragePW;
            } else {
                contractedActivityCategories[activity.category.name] =
                    durationAveragePW;
            }

            if (contractedActivityLocations[activity.location.name]) {
                contractedActivityLocations[activity.location.name] +=
                    durationAveragePW;
            } else {
                contractedActivityLocations[activity.location.name] =
                    durationAveragePW;
            }
        }
        if (activity.type.code === "PA") {
            durationWeeklyAverage.PA += durationAveragePW;
        } else if (activity.type.code === "APA") {
            durationWeeklyAverage.APA += durationAveragePW;
        }
        if (activityCategories[activity.category.name]) {
            activityCategories[activity.category.name] += durationAveragePW;
        } else {
            activityCategories[activity.category.name] = durationAveragePW;
        }
    });

    const dataSetTypes = {
        labels: [
            "Programmed Activity (PA)",
            "Additional Programmed Activity (APA)",
        ],
        values: [durationWeeklyAverage.PA, durationWeeklyAverage.APA],
    };

    const durationDailyTotals_PA = Object.values(
        contractedDurationDailyAverage.PA
    );
    const durationWeeklyTotal_PA = durationDailyTotals_PA.length
        ? durationDailyTotals_PA.reduce((sum, x) => sum + x)
        : 0;

    const durationDailyTotals_APA = Object.values(
        contractedDurationDailyAverage.APA
    );
    const durationWeeklyTotal_APA = durationDailyTotals_APA.length
        ? durationDailyTotals_APA.reduce((sum, x) => sum + x)
        : 0;

    const dataSetCategories = {
        labels: Object.keys(contractedActivityCategories),
        values: Object.values(activityCategories),
    };

    const dataSetLocations = {
        labels: Object.keys(contractedActivityLocations),
        values: Object.values(contractedActivityLocations),
    };

    const isCompleted = jobPlan.status === "signedoff";

    return (
        <Container className="content">
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <BreadcrumbBuilder />
                </Grid>
                <Grid item xs={12}>
                    <Card className="card">
                        <CardHeader
                            disableTypography
                            avatar={<JPAvatar type="jobplan" />}
                            action={
                                <Stack
                                    direction="row"
                                    justifyContent="flex-start"
                                    alignItems="center"
                                    spacing={2}
                                    sx={{ padding: "8px" }}
                                >
                                    <JobPlanButton
                                        id={jobPlan.id}
                                        status={jobPlan.status}
                                    />
                                </Stack>
                            }
                            title={
                                <Typography
                                    component="h3"
                                    className="card-header-title"
                                >
                                    {jobPlan.title || jobPlan.resourceType}
                                </Typography>
                            }
                            subheader={
                                <Typography
                                    component="span"
                                    className="card-header-subtitle"
                                >
                                    <RenderDate date={jobPlan?.effectiveDate} />
                                </Typography>
                            }
                        />
                        <CardContent>
                            <Box className="card-content">
                                <JobPlanDetails jobPlan={jobPlan} />
                            </Box>
                        </CardContent>
                        {isCompleted && (
                            <CardContent>
                                <Box className="card-content">
                                    <AlertMessage
                                        severity="success"
                                        message="This job plan has been signed off"
                                    />
                                </Box>
                            </CardContent>
                        )}
                    </Card>
                </Grid>
                <Grid item xs={6} sm={6} md={6}>
                    <Card className="card">
                        <CardHeader
                            disableTypography
                            title={
                                <Typography
                                    component="h3"
                                    className="card-header-title"
                                >
                                    Average p/w (PA) - &nbsp;
                                    <NumericFormat
                                        value={durationWeeklyTotal_PA}
                                        decimalSeparator="."
                                        displayType="text"
                                        decimalScale={3}
                                    />
                                </Typography>
                            }
                        />
                        <CardContent>
                            <ActivityWeeklyChart
                                data={durationDailyTotals_PA}
                                label="PA"
                            />
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={6} md={6}>
                    <Card className="card">
                        <CardHeader
                            disableTypography
                            title={
                                <Typography
                                    component="h3"
                                    className="card-header-title"
                                >
                                    Average p/w (APA) - &nbsp;
                                    <NumericFormat
                                        value={durationWeeklyTotal_APA}
                                        decimalSeparator="."
                                        displayType="text"
                                        decimalScale={3}
                                    />
                                </Typography>
                            }
                        />
                        <CardContent>
                            <ActivityWeeklyChart
                                data={durationDailyTotals_APA}
                                label="SPA"
                            />
                        </CardContent>
                    </Card>
                </Grid>
                {activityCount > 0 && (
                    <Grid item xs={12} sm={12} md={6} lg={6} xl={4}>
                        <Card className="card">
                            <CardHeader
                                disableTypography
                                title={
                                    <Typography
                                        component="h3"
                                        className="card-header-title"
                                    >
                                        Activities by type
                                    </Typography>
                                }
                            />
                            <CardContent>
                                <Box className="doughnut">
                                    <DoughnutChart
                                        labels={dataSetTypes.labels}
                                        values={dataSetTypes.values}
                                    />
                                </Box>
                                <Box className="card-content">
                                    <Typography
                                        variant="body1"
                                        className="text"
                                        sx={{ textAlign: "right" }}
                                    >
                                        {" activities "}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                )}
                {activityCount > 0 && (
                    <Grid item xs={12} sm={12} md={6} lg={6} xl={4}>
                        <Card className="card">
                            <CardHeader
                                disableTypography
                                title={
                                    <Typography
                                        component="h3"
                                        className="card-header-title"
                                    >
                                        Activities by category
                                    </Typography>
                                }
                            />
                            <CardContent>
                                <Box className="doughnut">
                                    <DoughnutChart
                                        labels={dataSetCategories.labels}
                                        values={dataSetCategories.values}
                                    />
                                </Box>
                                <Box className="card-content">
                                    <Typography
                                        variant="body1"
                                        className="text"
                                        sx={{ textAlign: "right" }}
                                    >
                                        {" activities "}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                )}
                {activityCount > 0 && (
                    <Grid item xs={12} sm={12} md={6} lg={6} xl={4}>
                        <Card className="card">
                            <CardHeader
                                disableTypography
                                title={
                                    <Typography
                                        component="h3"
                                        className="card-header-title"
                                    >
                                        Activities by location
                                    </Typography>
                                }
                            />
                            <CardContent>
                                <Box className="doughnut">
                                    <DoughnutChart
                                        labels={dataSetLocations.labels}
                                        values={dataSetLocations.values}
                                    />
                                </Box>
                                <Box className="card-content">
                                    <Typography
                                        variant="body1"
                                        className="text"
                                        sx={{ textAlign: "right" }}
                                    >
                                        {" activities "}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                )}
            </Grid>
        </Container>
    );
}

const JobPlanInsightsContainer = () => {
    const { id } = useParams();
    const dispatch = useDispatch();

    const jobPlan = useSelector((state) => getJobPlan(state, id));
    const error = useSelector((state) =>
        getError(state, ActionTypes.GET_JP_JOBPLAN_SUCCESS)
    );

    useEffect(() => {
        dispatch(getJobPlanDetails(appCode, id));
    }, [dispatch, id]);

    if (!!error) {
        if (error.errorCode === 404) {
            return <Error404 />;
        }
        if (error.errorCode === 500) {
            return <Error500 />;
        }
    }

    if (!jobPlan) return <LinearProgress />;

    return <JobPlanInsights jobPlan={jobPlan} />;
};

export default JobPlanInsightsContainer;
